import { UUID } from 'crypto';
import React, { useEffect, useState } from 'react';
import { useBunkerContext } from 'src/context/BunkerContext';
import { FuelRobVM } from 'src/types/ViewModel/Bunkering/FuelRobVM';
import { ColumnHeaderSubTitle, ColumnHeaderTitle, TotalRobBunkerListWrapper, TotalRobHeader, TotalRobWrapper } from './BunkerRob.styles';
import { getFuelType } from 'src/types/ViewModel/Bunkering/FuelType';

interface RobTotal {
    bunkeringGuid: UUID;
    total: number;
}

interface BunkerRobTotalProps {
    fuelRobVM: FuelRobVM;
}

const BunkerRobTotal: React.FC<BunkerRobTotalProps> = ({ fuelRobVM }) => {
    const { bunkerings } = useBunkerContext();
    const [robTotal, setRobTotal] = useState<RobTotal[]>();
    const [totalRobByFuelType, setTotalRobByFuelType] = useState<{ [key: number]: number }>({});

    useEffect(() => {
        const _bunkeringGuids = fuelRobVM.fuelRobColumns.flatMap((column) => column.fuelRobs.map((rob) => rob.bunkeringGuid));
        const uniqueBunkeringGuidsSet = new Set(_bunkeringGuids);
        const uniqueBunkeringGuids = Array.from(uniqueBunkeringGuidsSet.values());
        const _robTotal: RobTotal[] = uniqueBunkeringGuids.map((bunkeringGuid) => {
            const total = fuelRobVM.fuelRobColumns[fuelRobVM.fuelRobColumns.length - 1]?.fuelRobs?.reduce((acc, rob) => {
                if (rob.bunkeringGuid === bunkeringGuid) {
                    acc += rob.rob;
                }
                return acc;
            }, 0);
            return { bunkeringGuid, total };
        });
        setRobTotal(_robTotal);

        const fuelTypesOnboard = fuelRobVM.fuelRobColumns.flatMap((column) =>
            column.fuelRobs.map((rob) => bunkerings.find((b) => b.bunkeringGuid === rob.bunkeringGuid)?.fuelType)
        );
        const uniqueFuelTypesOnboard = new Set(fuelTypesOnboard);
        const _totalRobByFuelType: { [key: number]: number } = {};
        uniqueFuelTypesOnboard.forEach((fuelType) => {
            _totalRobByFuelType[fuelType] = fuelRobVM.fuelRobColumns[fuelRobVM.fuelRobColumns.length - 1]?.fuelRobs.reduce((acc, rob) => {
                if (bunkerings.find((b) => b.bunkeringGuid === rob.bunkeringGuid)?.fuelType === fuelType) {
                    acc += rob.rob;
                }
                return acc;
            }, 0);
        });
        setTotalRobByFuelType(_totalRobByFuelType);
    }, [fuelRobVM]);

    return (
        <TotalRobWrapper>
            <TotalRobHeader>
                <ColumnHeaderTitle>ROB per bunkering reference</ColumnHeaderTitle>
                <ColumnHeaderSubTitle>(At end of AoL period)</ColumnHeaderSubTitle>
            </TotalRobHeader>
            <TotalRobBunkerListWrapper>
                {robTotal?.map((rob) => (
                    <div key={rob.bunkeringGuid}>
                        <div style={{ fontWeight: 'bold' }}>{`${
                            bunkerings.find((bunkering) => bunkering.bunkeringGuid === rob.bunkeringGuid)?.bunkeringReference
                        }`}</div>
                        <ColumnHeaderSubTitle>{`${rob.total.toFixed(2)} MT`}</ColumnHeaderSubTitle>
                    </div>
                ))}
            </TotalRobBunkerListWrapper>
            <TotalRobHeader>
                <ColumnHeaderTitle>Total ROB</ColumnHeaderTitle>
                <ColumnHeaderSubTitle>(At end of AoL period)</ColumnHeaderSubTitle>
            </TotalRobHeader>
            <TotalRobBunkerListWrapper>
                {Object.keys(totalRobByFuelType).map((fuelType) => (
                    <div key={fuelType}>
                        <div style={{ fontWeight: 'bold' }}>{`Total ${getFuelType(
                            bunkerings.find((b) => b.fuelType === Number(fuelType))?.fuelType
                        )}`}</div>
                        <ColumnHeaderSubTitle>{`${totalRobByFuelType[fuelType].toFixed(2)} MT`}</ColumnHeaderSubTitle>
                    </div>
                ))}
                <div>
                    <div style={{ fontWeight: 'bold' }}>Total on board:</div>
                    <ColumnHeaderSubTitle>{`${robTotal?.reduce((acc, rob) => acc + rob.total, 0).toFixed(2)} MT`}</ColumnHeaderSubTitle>
                </div>
            </TotalRobBunkerListWrapper>
        </TotalRobWrapper>
    );
};

export default BunkerRobTotal;
